import { Box, Tab, Tabs } from '@mui/material'
import {
	createFileRoute,
	Link,
	Navigate,
	notFound,
	Outlet,
	redirect,
	useMatches,
	useParams,
} from '@tanstack/react-router'
import { ErrorComponent } from '@/components/ErrorComponent'
import {
	getEntityDefaultTab,
	getEntityDetailQueryOptions,
	getEntityTabs,
	resolveEntity,
} from '@/entity-config-runtime'
import { useAppAbility } from '@/integrations/casl/ability'
import {
	canReadEntityOrTab,
	filterReadableTabs,
} from '@/integrations/casl/route-ability'
import {
	getEntityRecordLabel,
	getEntityTabLabel,
} from '@/lib/entity-route-i18n'

function getEntityData(queryData: unknown, entityId: string) {
	if (!queryData || typeof queryData !== 'object' || Array.isArray(queryData)) {
		return undefined
	}

	const data = (queryData as Record<string, unknown>).data
	if (Array.isArray(data)) {
		return data.find(
			(item) =>
				item &&
				typeof item === 'object' &&
				String((item as Record<string, unknown>).id) === String(entityId),
		)
	}

	if (data && typeof data === 'object' && !Array.isArray(data)) {
		return data
	}

	return queryData
}

export const Route = createFileRoute('/_authenticated/$entity/$entityId')({
	beforeLoad: ({ params, context }) => {
		const entity = String(params.entity ?? '')
		if (!entity) {
			throw new Error('Missing entity route parameter')
		}

		const entityConfig = resolveEntity(entity)
		const hasReadableTab =
			filterReadableTabs(
				context.ability,
				entity,
				getEntityTabs(entityConfig).map((item) => ({
					label: item.key,
					value: item.key,
				})),
			).length > 0

		if (!canReadEntityOrTab(context.ability, entity) && !hasReadableTab) {
			throw notFound({})
		}
	},
	async loader({ params, location, context: { queryClient, ability } }) {
		const entityKey = String(params.entity ?? '')
		const entityId = String(params.entityId ?? '')
		const entityConfig = resolveEntity(entityKey)
		const tabs = getEntityTabs(entityConfig)
		const readableTabs = filterReadableTabs(
			ability,
			entityKey,
			tabs.map((item) => ({ label: item.key, value: item.key })),
		).map((item) => item.value)
		const queryData = await queryClient.ensureQueryData(
			getEntityDetailQueryOptions({
				params: { entity: entityKey, entityId },
				search: { skip: 0, take: 500 },
			}),
		)
		const currentEntity = getEntityData(queryData, entityId)

		if (!currentEntity) {
			throw notFound()
		}

		const label = getEntityRecordLabel(entityKey, currentEntity, entityId)
		const currentPath = location.pathname.replace(/\/$/, '')
		if (
			readableTabs.length > 0 &&
			currentPath === `/${entityKey}/${entityId}`
		) {
			throw redirect({
				to: `/${entityKey}/${entityId}/${readableTabs[0] ?? getEntityDefaultTab(entityConfig)}`,
			})
		}

		return {
			breadcrumb: {
				label,
			},
		}
	},
	component: EntityDetailLayout,
	errorComponent: ErrorComponent,
})

export default function EntityDetailLayout() {
	const ability = useAppAbility()
	const params = useParams({ strict: false })
	const entityKey = String(params.entity ?? '')
	const entityId = String(params.entityId ?? '')
	const entityConfig = resolveEntity(entityKey)
	const tabs = getEntityTabs(entityConfig)
	const readableTabs = filterReadableTabs(
		ability,
		entityKey,
		tabs.map((item) => ({ label: item.key, value: item.key })),
	)

	const matches = useMatches()
	const routeTab = params.tab ?? matches.at(-1)?.fullPath.split('/').at(-1)
	const currentTab = routeTab === 'details' ? 'overview' : routeTab

	if (currentTab === '$entityId') {
		// redirect to next readable tab
		const nextTab = readableTabs[0]?.value ?? 'overview'
		return <Navigate to={`/${entityKey}/${entityId}/${nextTab}`} replace />
	}

	return (
		<Box
			sx={{
				width: '100%',
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
				height: '100%',
				minHeight: 0,
				overflow: 'hidden',
			}}
		>
			<Tabs
				value={currentTab}
				variant="scrollable"
				scrollButtons="auto"
				sx={{ mb: 1 }}
			>
				{readableTabs.map((t) => (
					<Tab
						key={t.value}
						label={getEntityTabLabel(t.value)}
						value={t.value}
						component={Link}
						to={`/${entityKey}/${entityId}/${t.value}`}
					/>
				))}
			</Tabs>
			<Outlet />
		</Box>
	)
}
